import { useMemo } from 'react'
import { useLengthCheck } from './useLengthCheck'
import { useForbiddenWordCheck } from './useForbiddenWordCheck'
import { useStyleCheck } from './useStyleCheck'
import type { CompiledFieldRules, Violation } from './validation.types'

export type { Violation, CompiledFieldRules, CompiledPattern, CompiledStyleRule } from './validation.types'

export function useTextValidation(text: string, fieldRules: CompiledFieldRules | undefined) {
  const { charsUsed, maxChars, violation: lengthViolation } = useLengthCheck(
    text, fieldRules?.maxChars ?? 0,
  )
  const forbidden = useForbiddenWordCheck(text, fieldRules?.forbiddenPatterns)
  const style = useStyleCheck(text, fieldRules?.styleRules)

  const violations = useMemo<Violation[]>(() => {
    const all: Violation[] = [...forbidden, ...style]
    if (lengthViolation) all.unshift(lengthViolation)
    return all
  }, [lengthViolation, forbidden, style])

  const severity = useMemo<'ok' | 'warning' | 'error'>(() => {
    if (violations.some((v) => v.severity === 'error')) return 'error'
    return violations.length > 0 ? 'warning' : 'ok'
  }, [violations])

  return { violations, severity, charsUsed, maxChars }
}
